<script setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
    // Business partner uuid — carried into registration for referral tracking.
    inviterUuid: {
        type: String,
        required: false,
        default: '',
    },
    // Inviter branding, same shape as the register-welcome page:
    // { show_sender_name, sender_name, partner_logo_url, language, ... }
    welcome: {
        type: Object,
        required: false,
        default: null,
    },
});

// Public pre-signup "discover" experience. Everything is deterministic
// rule-based math + curated data — no AI calls, so it scales for free.
// Covers all platform languages (en/sv/no/de/es), matching onboarding-v2.

const LANG_STORAGE_KEY = 'myflow-discover-lang';

const messages = {
    en: {
        intro_kicker: 'Takes about 60 seconds',
        intro_heading: 'See what myflow does for you',
        intro_body: 'Answer four quick questions, and Flowie will show you how many hours you could save, how much more money you could make — and the AI team waiting to help.',
        intro_cta: "Let's go",

        q_role: "Hi! I'm Flowie 👋 What kind of business do you run?",
        role_coach: 'Coach',
        role_consultant: 'Consultant',
        role_creator: 'Creator / Educator',
        role_therapist: 'Therapist / Wellness',
        role_agency: 'Agency / Freelancer',
        role_craftsman: 'Carpenter / Tradesperson',
        role_health: 'Health / Fitness',
        role_beauty: 'Beauty / Salon',
        role_photographer: 'Photographer / Media',
        role_other: 'Something else',

        q_clients: 'Nice! How many clients do you work with in a typical month?',
        clients_a: '1–5',
        clients_b: '6–15',
        clients_c: '16–40',
        clients_d: '40+',

        q_admin: 'How much time do you spend on admin in a typical day — emails, invoices, quotes, updating your website?',
        admin_a: '15 minutes',
        admin_b: '30 minutes',
        admin_c: '45 minutes',
        admin_d: '1 hour',
        admin_e: '2+ hours',

        q_handoff: 'Last question. What would you like your new AI team to take off your plate? Pick as many as you like.',
        handoff_website: 'Build my website',
        handoff_leads: 'Find new clients',
        handoff_newsletter: 'Send my newsletters',
        handoff_content: 'Create content',
        handoff_automations: 'Automate follow-ups',
        handoff_sales: 'Follow up on quotes & sales',
        handoff_done: 'Show my results',

        q_name: "Great picks! Before I show your results — what's your name?",
        q_email: 'Nice to meet you, {name}! What email should your account use?',
        q_business: "And what's your business called?",
        q_country: 'Last detail — which country are you based in?',
        input_placeholder: 'Type your answer…',
        input_send: 'Send',

        invited_by: 'Personally invited by {name}',

        thinking_1: 'Reading your answers…',
        thinking_2: 'Sketching a website for a {role}…',
        thinking_3: 'Assembling your AI team…',
        thinking_4: 'Calculating hours and revenue…',
        thinking_5: 'Almost there…',

        reveal_heading: "Here's what myflow unlocks for you",
        stat_hours: 'hours back every month',
        stat_hours_sub: '{daily} of admin a day, handled by your AI team',
        stat_revenue: 'potential extra revenue / month',
        stat_revenue_sub: 'if you bill just half of those hours at a typical {role} rate',
        stat_tools: 'saved on tools / month',
        stat_tools_sub: 'replaces your website builder, email tool, CRM & e-signing',

        team_heading: 'Your AI team is ready',
        team_body: 'They already know what a {role} needs — and they start the minute you do.',
        other_team_heading: 'Also available on your team',
        other_team_body: 'Ready to jump in whenever you need them.',
        starts_heading: 'They would start with:',
        start_website: 'A new website, live in minutes',
        start_leads: 'A list of leads that fit your business',
        start_newsletter: 'Your first newsletter, drafted',
        start_content: 'A week of content, planned',
        start_automations: 'Follow-ups that send themselves',
        start_sales: 'Quotes and e-signing, ready to go',

        disclaimer: 'Estimates based on typical {role} businesses using myflow.',
        cta: 'Claim my free account',
        cta_sub: 'No credit card needed',
        restart: 'Start over',
    },
    sv: {
        intro_kicker: 'Tar cirka 60 sekunder',
        intro_heading: 'Se vad myflow gör för dig',
        intro_body: 'Svara på fyra snabba frågor, så visar Flowie hur många timmar du kan spara, hur mycket mer pengar du kan tjäna — och AI-teamet som väntar på att hjälpa dig.',
        intro_cta: 'Kör igång',

        q_role: 'Hej! Jag är Flowie 👋 Vad för slags verksamhet driver du?',
        role_coach: 'Coach',
        role_consultant: 'Konsult',
        role_creator: 'Kreatör / Utbildare',
        role_therapist: 'Terapeut / Hälsa',
        role_agency: 'Byrå / Frilansare',
        role_craftsman: 'Snickare / Hantverkare',
        role_health: 'Hälsa / Fitness',
        role_beauty: 'Skönhet / Salong',
        role_photographer: 'Fotograf / Media',
        role_other: 'Något annat',

        q_clients: 'Vad kul! Hur många kunder jobbar du med under en vanlig månad?',
        clients_a: '1–5',
        clients_b: '6–15',
        clients_c: '16–40',
        clients_d: '40+',

        q_admin: 'Hur mycket tid lägger du på admin en vanlig dag — mejl, fakturor, offerter, uppdatera hemsidan?',
        admin_a: '15 minuter',
        admin_b: '30 minuter',
        admin_c: '45 minuter',
        admin_d: '1 timme',
        admin_e: '2+ timmar',

        q_handoff: 'Sista frågan. Vad vill du att ditt nya AI-team ska sköta åt dig? Välj så många du vill.',
        handoff_website: 'Bygg min hemsida',
        handoff_leads: 'Hitta nya kunder',
        handoff_newsletter: 'Skicka mina nyhetsbrev',
        handoff_content: 'Skapa innehåll',
        handoff_automations: 'Automatisera uppföljningar',
        handoff_sales: 'Följ upp offerter & affärer',
        handoff_done: 'Visa mitt resultat',

        q_name: 'Bra val! Innan jag visar ditt resultat — vad heter du?',
        q_email: 'Kul att träffas, {name}! Vilken mejladress ska ditt konto ha?',
        q_business: 'Och vad heter din verksamhet?',
        q_country: 'Sista detaljen — vilket land utgår du från?',
        input_placeholder: 'Skriv ditt svar…',
        input_send: 'Skicka',

        invited_by: 'Personligen inbjuden av {name}',

        thinking_1: 'Läser dina svar…',
        thinking_2: 'Skissar en hemsida för en {role}…',
        thinking_3: 'Sätter ihop ditt AI-team…',
        thinking_4: 'Räknar på timmar och intäkter…',
        thinking_5: 'Nästan klart…',

        reveal_heading: 'Det här låser myflow upp för dig',
        stat_hours: 'timmar tillbaka varje månad',
        stat_hours_sub: '{daily} admin om dagen, som ditt AI-team sköter',
        stat_revenue: 'möjlig extra intäkt / månad',
        stat_revenue_sub: 'om du fakturerar bara hälften av timmarna till en vanlig timpeng för en {role}',
        stat_tools: 'sparat på verktyg / månad',
        stat_tools_sub: 'ersätter hemsidesverktyg, mejlverktyg, CRM & e-signering',

        team_heading: 'Ditt AI-team står redo',
        team_body: 'De vet redan vad en {role} behöver — och de sätter igång i samma stund som du gör det.',
        other_team_heading: 'Finns också i ditt team',
        other_team_body: 'Redo att hoppa in när du behöver dem.',
        starts_heading: 'De skulle börja med:',
        start_website: 'En ny hemsida, live på minuter',
        start_leads: 'En lista med leads som passar din verksamhet',
        start_newsletter: 'Ditt första nyhetsbrev, utkastat',
        start_content: 'En veckas innehåll, planerat',
        start_automations: 'Uppföljningar som skickar sig själva',
        start_sales: 'Offerter och e-signering, redo att skickas',

        disclaimer: 'Uppskattningar baserade på typiska verksamheter som din på myflow.',
        cta: 'Skapa mitt gratiskonto',
        cta_sub: 'Inget kreditkort behövs',
        restart: 'Börja om',
    },
    no: {
        intro_kicker: 'Tar cirka 60 sekunder',
        intro_heading: 'Se hva myflow gjør for deg',
        intro_body: 'Svar på fire raske spørsmål, så viser Flowie deg hvor mange timer du kan spare, hvor mye mer penger du kan tjene — og AI-teamet som venter på å hjelpe deg.',
        intro_cta: 'Sett i gang',

        q_role: 'Hei! Jeg er Flowie 👋 Hva slags virksomhet driver du?',
        role_coach: 'Coach',
        role_consultant: 'Konsulent',
        role_creator: 'Skaper / Kursholder',
        role_therapist: 'Terapeut / Helse',
        role_agency: 'Byrå / Frilanser',
        role_craftsman: 'Snekker / Håndverker',
        role_health: 'Helse / Trening',
        role_beauty: 'Skjønnhet / Salong',
        role_photographer: 'Fotograf / Media',
        role_other: 'Noe annet',

        q_clients: 'Så bra! Hvor mange kunder jobber du med i en vanlig måned?',
        clients_a: '1–5',
        clients_b: '6–15',
        clients_c: '16–40',
        clients_d: '40+',

        q_admin: 'Hvor mye tid bruker du på admin en vanlig dag — e-post, fakturaer, tilbud, oppdatere nettsiden?',
        admin_a: '15 minutter',
        admin_b: '30 minutter',
        admin_c: '45 minutter',
        admin_d: '1 time',
        admin_e: '2+ timer',

        q_handoff: 'Siste spørsmål. Hva vil du at det nye AI-teamet ditt skal ta seg av for deg? Velg så mange du vil.',
        handoff_website: 'Bygg nettsiden min',
        handoff_leads: 'Finn nye kunder',
        handoff_newsletter: 'Send nyhetsbrevene mine',
        handoff_content: 'Lag innhold',
        handoff_automations: 'Automatiser oppfølginger',
        handoff_sales: 'Følg opp tilbud & salg',
        handoff_done: 'Vis resultatet mitt',

        q_name: 'Gode valg! Før jeg viser resultatet ditt — hva heter du?',
        q_email: 'Hyggelig å møte deg, {name}! Hvilken e-postadresse skal kontoen din bruke?',
        q_business: 'Og hva heter virksomheten din?',
        q_country: 'Siste detalj — hvilket land holder du til i?',
        input_placeholder: 'Skriv svaret ditt…',
        input_send: 'Send',

        invited_by: 'Personlig invitert av {name}',

        thinking_1: 'Leser svarene dine…',
        thinking_2: 'Skisserer en nettside for en {role}…',
        thinking_3: 'Setter sammen AI-teamet ditt…',
        thinking_4: 'Regner på timer og inntekter…',
        thinking_5: 'Nesten klart…',

        reveal_heading: 'Dette låser myflow opp for deg',
        stat_hours: 'timer tilbake hver måned',
        stat_hours_sub: '{daily} admin om dagen, som AI-teamet ditt tar seg av',
        stat_revenue: 'mulig ekstra inntekt / måned',
        stat_revenue_sub: 'hvis du fakturerer bare halvparten av timene til vanlig timepris for en {role}',
        stat_tools: 'spart på verktøy / måned',
        stat_tools_sub: 'erstatter nettsideverktøy, e-postverktøy, CRM & e-signering',

        team_heading: 'AI-teamet ditt står klart',
        team_body: 'De vet allerede hva en {role} trenger — og de setter i gang i samme øyeblikk som deg.',
        other_team_heading: 'Også tilgjengelig i teamet ditt',
        other_team_body: 'Klare til å hoppe inn når du trenger dem.',
        starts_heading: 'De ville startet med:',
        start_website: 'En ny nettside, live på minutter',
        start_leads: 'En liste med leads som passer virksomheten din',
        start_newsletter: 'Ditt første nyhetsbrev, som utkast',
        start_content: 'En uke med innhold, planlagt',
        start_automations: 'Oppfølginger som sender seg selv',
        start_sales: 'Tilbud og e-signering, klart til å sendes',

        disclaimer: 'Estimater basert på typiske virksomheter som din på myflow.',
        cta: 'Opprett gratiskontoen min',
        cta_sub: 'Ingen kredittkort nødvendig',
        restart: 'Start på nytt',
    },
    de: {
        intro_kicker: 'Dauert etwa 60 Sekunden',
        intro_heading: 'Sieh, was myflow für dich tut',
        intro_body: 'Beantworte vier kurze Fragen, und Flowie zeigt dir, wie viele Stunden du sparen und wie viel mehr Geld du verdienen könntest — und das KI-Team, das darauf wartet, dir zu helfen.',
        intro_cta: "Los geht's",

        q_role: 'Hi! Ich bin Flowie 👋 Was für ein Business führst du?',
        role_coach: 'Coach',
        role_consultant: 'Berater',
        role_creator: 'Creator / Trainer',
        role_therapist: 'Therapeut / Wellness',
        role_agency: 'Agentur / Freelancer',
        role_craftsman: 'Tischler / Handwerker',
        role_health: 'Gesundheit / Fitness',
        role_beauty: 'Beauty / Salon',
        role_photographer: 'Fotograf / Medien',
        role_other: 'Etwas anderes',

        q_clients: 'Super! Mit wie vielen Kunden arbeitest du in einem typischen Monat?',
        clients_a: '1–5',
        clients_b: '6–15',
        clients_c: '16–40',
        clients_d: '40+',

        q_admin: 'Wie viel Zeit steckst du an einem typischen Tag in Admin — E-Mails, Rechnungen, Angebote, deine Website?',
        admin_a: '15 Minuten',
        admin_b: '30 Minuten',
        admin_c: '45 Minuten',
        admin_d: '1 Stunde',
        admin_e: '2+ Stunden',

        q_handoff: 'Letzte Frage. Was soll dein neues KI-Team dir abnehmen? Wähl so viele du willst.',
        handoff_website: 'Meine Website bauen',
        handoff_leads: 'Neue Kunden finden',
        handoff_newsletter: 'Meine Newsletter versenden',
        handoff_content: 'Content erstellen',
        handoff_automations: 'Follow-ups automatisieren',
        handoff_sales: 'Angebote & Abschlüsse nachfassen',
        handoff_done: 'Zeig mir mein Ergebnis',

        q_name: 'Gute Wahl! Bevor ich dein Ergebnis zeige — wie heißt du?',
        q_email: 'Schön dich kennenzulernen, {name}! Welche E-Mail-Adresse soll dein Konto nutzen?',
        q_business: 'Und wie heißt dein Business?',
        q_country: 'Letztes Detail — in welchem Land bist du zuhause?',
        input_placeholder: 'Deine Antwort…',
        input_send: 'Senden',

        invited_by: 'Persönlich eingeladen von {name}',

        thinking_1: 'Lese deine Antworten…',
        thinking_2: 'Skizziere eine Website für {role}…',
        thinking_3: 'Stelle dein KI-Team zusammen…',
        thinking_4: 'Rechne Stunden und Umsatz durch…',
        thinking_5: 'Fast fertig…',

        reveal_heading: 'Das schaltet myflow für dich frei',
        stat_hours: 'Stunden zurück – jeden Monat',
        stat_hours_sub: '{daily} Admin pro Tag, die dein KI-Team übernimmt',
        stat_revenue: 'möglicher Mehrumsatz / Monat',
        stat_revenue_sub: 'wenn du nur die Hälfte dieser Stunden zum üblichen Stundensatz abrechnest',
        stat_tools: 'gespart an Tools / Monat',
        stat_tools_sub: 'ersetzt Website-Baukasten, E-Mail-Tool, CRM & E-Signatur',

        team_heading: 'Dein KI-Team steht bereit',
        team_body: 'Sie wissen schon, was du als {role} brauchst — und legen los, sobald du es tust.',
        other_team_heading: 'Ebenfalls in deinem Team verfügbar',
        other_team_body: 'Bereit einzuspringen, wann immer du sie brauchst.',
        starts_heading: 'Womit sie starten würden:',
        start_website: 'Eine neue Website, live in Minuten',
        start_leads: 'Eine Lead-Liste, die zu deinem Business passt',
        start_newsletter: 'Dein erster Newsletter, als Entwurf',
        start_content: 'Eine Woche Content, geplant',
        start_automations: 'Follow-ups, die sich selbst versenden',
        start_sales: 'Angebote und E-Signatur, startklar',

        disclaimer: 'Schätzungen auf Basis typischer Unternehmen wie deinem auf myflow.',
        cta: 'Mein Gratis-Konto sichern',
        cta_sub: 'Keine Kreditkarte nötig',
        restart: 'Von vorn beginnen',
    },
    es: {
        intro_kicker: 'Tarda unos 60 segundos',
        intro_heading: 'Descubre lo que myflow hace por ti',
        intro_body: 'Responde cuatro preguntas rápidas y Flowie te mostrará cuántas horas podrías ahorrar, cuánto dinero más podrías ganar — y el equipo de IA que te espera para ayudarte.',
        intro_cta: 'Vamos',

        q_role: '¡Hola! Soy Flowie 👋 ¿Qué tipo de negocio tienes?',
        role_coach: 'Coach',
        role_consultant: 'Consultor/a',
        role_creator: 'Creador/a / Formador/a',
        role_therapist: 'Terapeuta / Bienestar',
        role_agency: 'Agencia / Freelance',
        role_craftsman: 'Carpintero / Oficios',
        role_health: 'Salud / Fitness',
        role_beauty: 'Belleza / Salón',
        role_photographer: 'Fotógrafo / Medios',
        role_other: 'Otra cosa',

        q_clients: '¡Genial! ¿Con cuántos clientes trabajas en un mes normal?',
        clients_a: '1–5',
        clients_b: '6–15',
        clients_c: '16–40',
        clients_d: '40+',

        q_admin: '¿Cuánto tiempo dedicas a la administración en un día normal — correos, facturas, presupuestos, tu web?',
        admin_a: '15 minutos',
        admin_b: '30 minutos',
        admin_c: '45 minutos',
        admin_d: '1 hora',
        admin_e: '2+ horas',

        q_handoff: 'Última pregunta. ¿De qué te gustaría que se encargara tu nuevo equipo de IA? Elige todas las que quieras.',
        handoff_website: 'Crear mi página web',
        handoff_leads: 'Encontrar nuevos clientes',
        handoff_newsletter: 'Enviar mis newsletters',
        handoff_content: 'Crear contenido',
        handoff_automations: 'Automatizar seguimientos',
        handoff_sales: 'Hacer seguimiento de presupuestos y ventas',
        handoff_done: 'Ver mis resultados',

        q_name: '¡Buena elección! Antes de mostrarte tu resultado — ¿cómo te llamas?',
        q_email: '¡Encantado, {name}! ¿Qué correo quieres usar para tu cuenta?',
        q_business: '¿Y cómo se llama tu negocio?',
        q_country: 'Último detalle — ¿desde qué país trabajas?',
        input_placeholder: 'Escribe tu respuesta…',
        input_send: 'Enviar',

        invited_by: 'Invitación personal de {name}',

        thinking_1: 'Leyendo tus respuestas…',
        thinking_2: 'Diseñando una web para {role}…',
        thinking_3: 'Formando tu equipo de IA…',
        thinking_4: 'Calculando horas e ingresos…',
        thinking_5: 'Casi listo…',

        reveal_heading: 'Esto es lo que myflow desbloquea para ti',
        stat_hours: 'horas recuperadas cada mes',
        stat_hours_sub: '{daily} de administración al día, gestionados por tu equipo de IA',
        stat_revenue: 'ingresos extra potenciales / mes',
        stat_revenue_sub: 'si facturas solo la mitad de esas horas a una tarifa típica de {role}',
        stat_tools: 'ahorrados en herramientas / mes',
        stat_tools_sub: 'sustituye tu creador de webs, herramienta de email, CRM y firma electrónica',

        team_heading: 'Tu equipo de IA está listo',
        team_body: 'Ya saben lo que necesita un/a {role} — y empiezan en cuanto tú lo hagas.',
        other_team_heading: 'También disponibles en tu equipo',
        other_team_body: 'Listos para ayudarte cuando los necesites.',
        starts_heading: 'Empezarían con:',
        start_website: 'Una web nueva, en línea en minutos',
        start_leads: 'Una lista de leads a medida de tu negocio',
        start_newsletter: 'Tu primera newsletter, redactada',
        start_content: 'Una semana de contenido, planificada',
        start_automations: 'Seguimientos que se envían solos',
        start_sales: 'Presupuestos y firma electrónica, listos',

        disclaimer: 'Estimaciones basadas en negocios como el tuyo en myflow.',
        cta: 'Crear mi cuenta gratis',
        cta_sub: 'Sin tarjeta de crédito',
        restart: 'Volver a empezar',
    },
};

const availableLanguages = [
    { code: 'en', label: '🇬🇧' },
    { code: 'sv', label: '🇸🇪' },
    { code: 'no', label: '🇳🇴' },
    { code: 'de', label: '🇩🇪' },
    { code: 'es', label: '🇪🇸' },
];

const storedLanguage = (() => {
    try {
        const v = window.localStorage?.getItem(LANG_STORAGE_KEY);
        return v && messages[v] ? v : null;
    } catch (e) {
        return null;
    }
})();

const browserLanguage = (navigator.language || 'en').slice(0, 2);
const userLanguage = ref(storedLanguage);

const activeLanguage = computed(() => {
    if (userLanguage.value && messages[userLanguage.value]) {
        return userLanguage.value;
    }
    if (props.welcome?.language && messages[props.welcome.language]) {
        return props.welcome.language;
    }
    return messages[browserLanguage] ? browserLanguage : 'en';
});

const showInviterKicker = computed(
    () => !!(props.welcome?.show_sender_name && props.welcome?.sender_name)
);

const setLanguage = (code) => {
    if (!messages[code]) return;
    userLanguage.value = code;
    try {
        window.localStorage?.setItem(LANG_STORAGE_KEY, code);
    } catch (e) {
        // ignore
    }
};

const t = (key, params = {}) => {
    let str = messages[activeLanguage.value]?.[key] ?? messages.en[key] ?? key;
    Object.keys(params).forEach(k => {
        str = str.replace(`{${k}}`, params[k]);
    });
    return str;
};

// ---------------------------------------------------------------------------
// Rule tables — the entire "intelligence" of this page.
// ---------------------------------------------------------------------------

const roles = [
    { key: 'coach', emoji: '🎯', hourlyRateEur: 90 },
    { key: 'consultant', emoji: '💼', hourlyRateEur: 110 },
    { key: 'creator', emoji: '🎬', hourlyRateEur: 70 },
    { key: 'therapist', emoji: '🌿', hourlyRateEur: 80 },
    { key: 'agency', emoji: '🚀', hourlyRateEur: 100 },
    { key: 'craftsman', emoji: '🔨', hourlyRateEur: 70 },
    { key: 'health', emoji: '💪', hourlyRateEur: 75 },
    { key: 'beauty', emoji: '💇', hourlyRateEur: 60 },
    { key: 'photographer', emoji: '📸', hourlyRateEur: 85 },
    { key: 'other', emoji: '✨', hourlyRateEur: 85 },
];

const clientOptions = [
    { key: 'clients_a' },
    { key: 'clients_b' },
    { key: 'clients_c' },
    { key: 'clients_d' },
];

const adminOptions = [
    { key: 'admin_a', hoursPerDay: 0.25 },
    { key: 'admin_b', hoursPerDay: 0.5 },
    { key: 'admin_c', hoursPerDay: 0.75 },
    { key: 'admin_d', hoursPerDay: 1 },
    { key: 'admin_e', hoursPerDay: 2 },
];

const handoffOptions = [
    { key: 'website', teammate: 'Web Designer', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/web-designer-v1.webp', bubbleClass: 'bg-aiAgentWebDesigner/20 border-aiAgentWebDesigner/40', startKey: 'start_website' },
    { key: 'leads', teammate: 'Lead Finder', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/lead-finder-v1.webp', bubbleClass: 'bg-aiAgentLeadFinder/20 border-aiAgentLeadFinder/40', startKey: 'start_leads' },
    { key: 'newsletter', teammate: 'Newsletter Specialist', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/newsletter-specialist-v1.webp', bubbleClass: 'bg-aiAgentNewsletterSpecialist/20 border-aiAgentNewsletterSpecialist/40', startKey: 'start_newsletter' },
    { key: 'content', teammate: 'Content Creator', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/content-creator-v1.webp', bubbleClass: 'bg-aiAgentContentCreator/20 border-aiAgentContentCreator/40', startKey: 'start_content' },
    { key: 'automations', teammate: 'Automation Expert', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/automation-expert-v1.webp', bubbleClass: 'bg-aiAgentAutomationExpert/20 border-aiAgentAutomationExpert/40', startKey: 'start_automations' },
    { key: 'sales', teammate: 'Sales Assistant', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/sales-assistant-v1.webp', bubbleClass: 'bg-aiAgentSalesAssistant/20 border-aiAgentSalesAssistant/40', startKey: 'start_sales' },
];

// ponytail: naive fixed FX + flat tool-savings figure; refine per-market if this converts.
const currencies = {
    en: { code: 'EUR', fx: 1, toolSavings: 70, locale: 'en-IE' },
    sv: { code: 'SEK', fx: 11, toolSavings: 800, locale: 'sv-SE' },
    no: { code: 'NOK', fx: 11.5, toolSavings: 800, locale: 'nb-NO' },
    de: { code: 'EUR', fx: 1, toolSavings: 70, locale: 'de-DE' },
    es: { code: 'EUR', fx: 1, toolSavings: 70, locale: 'es-ES' },
};

// ---------------------------------------------------------------------------
// Flow state
// ---------------------------------------------------------------------------

const stage = ref('intro'); // intro | chat | thinking | reveal
const chatLog = ref([]); // { from: 'flowie'|'user', text }
const typing = ref(false);
const currentQuestion = ref(null); // 'role' | 'clients' | 'admin' | 'handoff' | 'name' | 'email' | 'business' | 'country'
const answers = ref({ role: null, clients: null, adminHours: null, adminKey: null, handoff: [], name: '', email: '', business: '', country: '', countryCode: '' });
const chatEnd = ref(null);

const questionOrder = ['role', 'clients', 'admin', 'handoff', 'name', 'email', 'business', 'country'];
const progress = computed(() => {
    if (stage.value === 'intro') return 0;
    if (stage.value !== 'chat') return 100;
    const idx = questionOrder.indexOf(currentQuestion.value);
    return Math.round((idx / questionOrder.length) * 100);
});

const scrollToEnd = () => {
    nextTick(() => chatEnd.value?.scrollIntoView({ behavior: 'smooth', block: 'end' }));
};

const askQuestion = (question) => {
    typing.value = true;
    currentQuestion.value = null;
    scrollToEnd();
    setTimeout(() => {
        typing.value = false;
        chatLog.value.push({ from: 'flowie', text: t(`q_${question}`, { name: (answers.value.name || '').split(' ')[0] }) });
        currentQuestion.value = question;
        scrollToEnd();
    }, 900);
};

const begin = () => {
    stage.value = 'chat';
    askQuestion('role');
};

const answer = (label, next) => {
    chatLog.value.push({ from: 'user', text: label });
    if (next) {
        askQuestion(next);
    }
};

const pickRole = (role) => {
    answers.value.role = role;
    answer(t(`role_${role.key}`), 'clients');
};

const pickClients = (option) => {
    answers.value.clients = option;
    answer(t(option.key), 'admin');
};

const pickAdmin = (option) => {
    answers.value.adminHours = option.hoursPerDay;
    answers.value.adminKey = option.key;
    answer(t(option.key), 'handoff');
};

const toggleHandoff = (option) => {
    const list = answers.value.handoff;
    const idx = list.indexOf(option.key);
    idx === -1 ? list.push(option.key) : list.splice(idx, 1);
};

// ---------------------------------------------------------------------------
// Contact questions — free-text chat answers that prefill the register page.
// ---------------------------------------------------------------------------

const contactQuestions = ['name', 'email', 'business', 'country'];
const contactInput = ref('');
const contactField = ref(null);

const isContactQuestion = computed(() => contactQuestions.includes(currentQuestion.value));

const contactInputValid = computed(() => {
    const value = contactInput.value.trim();
    if (currentQuestion.value === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    }
    return value.length > 0;
});

const submitContact = () => {
    if (!isContactQuestion.value || !contactInputValid.value || currentQuestion.value === 'country') return;

    const question = currentQuestion.value;
    const value = contactInput.value.trim();
    answers.value[question] = value;
    contactInput.value = '';

    answer(value, { name: 'email', email: 'business', business: 'country' }[question]);
};

// Country goes through the input-language normaliser — selecting a match answers.
const pickCountry = (lang) => {
    answers.value.country = lang.label;
    answers.value.countryCode = lang.code;
    answer(lang.label, null);
    saveContact();
    startThinking();
};

const saveContact = () => {
    try {
        window.sessionStorage?.setItem('myflow-discover-contact', JSON.stringify({
            name: answers.value.name,
            email: answers.value.email,
            company_name: answers.value.business,
            country: answers.value.country,
            country_code: answers.value.countryCode,
            handoff: answers.value.handoff,
        }));
    } catch (e) {
        // ignore — the register page falls back to empty fields
    }
};

watch(currentQuestion, (question) => {
    if (contactQuestions.includes(question)) {
        nextTick(() => {
            (contactField.value ?? document.querySelector('.discover-country input[type="text"]'))?.focus();
        });
    }
});

// ---------------------------------------------------------------------------
// Thinking stage (pure theatre)
// ---------------------------------------------------------------------------

const thinkingIndex = ref(0);
const thinkingProgress = ref(0);
const thinkingKeys = ['thinking_1', 'thinking_2', 'thinking_3', 'thinking_4', 'thinking_5'];

const finishQuestions = () => {
    answer(t('handoff_done'), 'name');
};

const startThinking = () => {
    currentQuestion.value = null;
    stage.value = 'thinking';
    thinkingIndex.value = 0;
    thinkingProgress.value = 0;
    const interval = setInterval(() => {
        thinkingIndex.value++;
        thinkingProgress.value = Math.min(95, (thinkingIndex.value / thinkingKeys.length) * 100);
        if (thinkingIndex.value >= thinkingKeys.length) {
            clearInterval(interval);
            stage.value = 'reveal';
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, 950);
};

const roleLabel = computed(() =>
    answers.value.role ? t(`role_${answers.value.role.key}`) : ''
);

// ---------------------------------------------------------------------------
// The value engine — plain arithmetic on the rule tables above.
// ---------------------------------------------------------------------------

const currency = computed(() => currencies[activeLanguage.value] ?? currencies.en);

const formatMoney = (amount) =>
    new Intl.NumberFormat(currency.value.locale, {
        style: 'currency',
        currency: currency.value.code,
        maximumFractionDigits: 0,
    }).format(amount);

// Their own daily answer, echoed under the hours stat so the math is obvious.
const dailyLabel = computed(() =>
    answers.value.adminKey ? t(answers.value.adminKey) : ''
);

const results = computed(() => {
    // Daily admin time × 22 workdays — the AI team handles all of it.
    const adminHoursPerDay = answers.value.adminHours ?? 0.5;
    const rateEur = answers.value.role?.hourlyRateEur ?? 85;
    const hoursSaved = Math.round(adminHoursPerDay * 22);
    const rate = rateEur * currency.value.fx;
    const extraRevenue = Math.round((hoursSaved * 0.5 * rate) / 100) * 100;

    return {
        hoursSaved,
        extraRevenue: formatMoney(extraRevenue),
        toolSavings: formatMoney(currency.value.toolSavings),
    };
});

const selectedTeam = computed(() => {
    const picked = handoffOptions.filter(o => answers.value.handoff.includes(o.key));
    return picked.length ? picked : handoffOptions.slice(0, 3);
});

// Teammates with no handoff question of their own — only shown as available.
const extraTeammates = [
    { key: 'data-analyst', teammate: 'Data Analyst', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/data-analyst-v1.webp' },
    { key: 'relationship-manager', teammate: 'Relationship Manager', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/relationship-manager-v1.webp' },
];

const availableTeam = computed(() => [
    ...handoffOptions.filter(o => !selectedTeam.value.includes(o)),
    ...extraTeammates,
]);

const restart = () => {
    chatLog.value = [];
    answers.value = { role: null, clients: null, adminHours: null, adminKey: null, handoff: [], name: '', email: '', business: '', country: '', countryCode: '' };
    stage.value = 'intro';
};

const registerLink = computed(() => {
    const baseUrl = props.inviterUuid
        ? route('expert.register.shortcut', { inviter: props.inviterUuid })
        : route('expert.register.shortcut');
    const params = new URLSearchParams({ utm_source: 'discover' });

    // Carry the answers into registration so Flowie can pick up where we left off.
    if (answers.value.role) params.set('d_role', answers.value.role.key);
    if (answers.value.clients) params.set('d_clients', answers.value.clients.key);
    if (answers.value.adminHours) params.set('d_admin', String(answers.value.adminHours));
    if (answers.value.handoff.length) params.set('d_handoff', answers.value.handoff.join(','));

    return `${baseUrl}?${params.toString()}`;
});
</script>
<template>
    <div class="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
        <!-- Top bar -->
        <div class="flex items-center justify-between px-4 py-3 max-w-3xl mx-auto">
            <div class="flex items-center">
                <template v-if="welcome?.partner_logo_url">
                    <img :src="welcome.partner_logo_url" alt="Partner logo" class="h-8 object-contain" />
                    <div class="h-6 w-px bg-gray-300 mx-3"></div>
                </template>
                <img
                    src="https://myflow.fra1.digitaloceanspaces.com/myflow-brand/logo-purple.svg"
                    alt="myflow"
                    class="w-24"
                />
            </div>
            <div class="flex items-center text-xs font-medium border border-gray-200 rounded-full overflow-hidden">
                <button
                    v-for="lang in availableLanguages"
                    :key="lang.code"
                    type="button"
                    class="px-3 py-1.5 text-2xl leading-none transition-all duration-150"
                    :class="activeLanguage === lang.code
                        ? 'bg-myflowLavender scale-110'
                        : 'bg-white hover:bg-gray-50 grayscale-[35%] hover:grayscale-0'"
                    :aria-label="lang.code"
                    @click="setLanguage(lang.code)"
                >
                    {{ lang.label }}
                </button>
            </div>
        </div>

        <div class="max-w-2xl mx-auto px-4 pb-24">
            <!-- Intro -->
            <div v-if="stage === 'intro'" class="text-center pt-12 md:pt-20 generating-appear">
                <ai-agent-ai-orchestrator-avatar class="w-24 h-24 md:w-32 md:h-32 mx-auto"></ai-agent-ai-orchestrator-avatar>
                <p
                    v-if="showInviterKicker"
                    class="mt-6 text-lg md:text-xl font-bold text-myflowPurple"
                >
                    {{ t('invited_by', { name: welcome.sender_name }) }}
                </p>
                <p
                    class="text-xs font-semibold uppercase tracking-widest text-myflowPurple"
                    :class="showInviterKicker ? 'mt-2' : 'mt-6'"
                >
                    {{ t('intro_kicker') }}
                </p>
                <h1 class="mt-3 text-3xl md:text-5xl font-bold text-gray-900" style="line-height: 1.15em;">
                    {{ t('intro_heading') }}
                </h1>
                <p class="mt-5 text-lg text-gray-700 font-light max-w-lg mx-auto">
                    {{ t('intro_body') }}
                </p>
                <button
                    type="button"
                    class="mt-8 px-8 py-3 text-white rounded-xl bg-myflowPurple text-lg shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200"
                    @click="begin"
                >
                    {{ t('intro_cta') }}
                </button>
            </div>

            <!-- Chat -->
            <div v-if="stage === 'chat' || stage === 'thinking'" class="pt-6">
                <progress-bar
                    :progress="stage === 'thinking' ? thinkingProgress : progress"
                    container-class="p-0 rounded-full overflow-hidden"
                    bar-class="bg-myflowPurple"
                ></progress-bar>

                <div class="mt-8">
                    <div
                        v-for="(message, i) in chatLog"
                        :key="i"
                        class="flex gap-3 mb-3 generating-appear"
                        :class="message.from === 'user' ? 'justify-end' : ''"
                    >
                        <div
                            v-if="message.from === 'flowie'"
                            class="flex-shrink-0 w-8 h-8 rounded-full bg-wlPrimary/10 flex items-center justify-center"
                        >
                            <ai-agent-ai-orchestrator-avatar class="w-6 h-6"></ai-agent-ai-orchestrator-avatar>
                        </div>
                        <div
                            class="px-4 py-3 rounded-lg max-w-[80%]"
                            :class="message.from === 'user'
                                ? 'bg-wlPrimary text-white'
                                : 'bg-gray-50 text-gray-700'"
                        >
                            {{ message.text }}
                        </div>
                    </div>

                    <!-- Typing indicator -->
                    <div v-if="typing" class="flex gap-3 mb-3 generating-appear">
                        <div class="flex-shrink-0 w-8 h-8 rounded-full bg-wlPrimary/10 flex items-center justify-center">
                            <ai-agent-ai-orchestrator-avatar class="w-6 h-6"></ai-agent-ai-orchestrator-avatar>
                        </div>
                        <div class="px-4 py-3 rounded-lg bg-gray-50 flex items-center gap-1">
                            <span class="typing-dot"></span>
                            <span class="typing-dot" style="animation-delay: 0.15s"></span>
                            <span class="typing-dot" style="animation-delay: 0.3s"></span>
                        </div>
                    </div>

                    <!-- Answer bubbles -->
                    <div v-if="currentQuestion === 'role'" class="mt-6 flex flex-wrap gap-2.5 generating-appear">
                        <button
                            v-for="role in roles"
                            :key="role.key"
                            type="button"
                            class="flowie-example-bubble inline-flex items-center gap-2 border bg-white/70 px-4 py-2.5 text-sm text-gray-800 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                            @click="pickRole(role)"
                        >
                            <span>{{ role.emoji }}</span>
                            <span>{{ t(`role_${role.key}`) }}</span>
                        </button>
                    </div>

                    <div v-if="currentQuestion === 'clients'" class="mt-6 flex flex-wrap gap-2.5 generating-appear">
                        <button
                            v-for="option in clientOptions"
                            :key="option.key"
                            type="button"
                            class="flowie-example-bubble inline-flex items-center border bg-white/70 px-5 py-2.5 text-sm text-gray-800 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                            @click="pickClients(option)"
                        >
                            {{ t(option.key) }}
                        </button>
                    </div>

                    <div v-if="currentQuestion === 'admin'" class="mt-6 flex flex-wrap gap-2.5 generating-appear">
                        <button
                            v-for="option in adminOptions"
                            :key="option.key"
                            type="button"
                            class="flowie-example-bubble inline-flex items-center border bg-white/70 px-5 py-2.5 text-sm text-gray-800 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                            @click="pickAdmin(option)"
                        >
                            {{ t(option.key) }}
                        </button>
                    </div>

                    <div v-if="currentQuestion === 'handoff'" class="mt-6 generating-appear">
                        <div class="flex flex-wrap gap-2.5">
                            <button
                                v-for="option in handoffOptions"
                                :key="option.key"
                                type="button"
                                class="flowie-example-bubble inline-flex items-center gap-2 border px-4 py-2.5 text-sm text-gray-800 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                                :class="[
                                    option.bubbleClass,
                                    answers.handoff.includes(option.key) ? 'ring-2 ring-myflowPurple' : '',
                                ]"
                                @click="toggleHandoff(option)"
                            >
                                <span>{{ t(`handoff_${option.key}`) }}</span>
                                <span v-if="answers.handoff.includes(option.key)">✓</span>
                            </button>
                        </div>
                        <button
                            type="button"
                            class="mt-6 px-6 py-2.5 text-white rounded-xl bg-myflowPurple shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200 disabled:opacity-40 disabled:hover:translate-y-0"
                            :disabled="answers.handoff.length === 0"
                            @click="finishQuestions"
                        >
                            {{ t('handoff_done') }}
                        </button>
                    </div>

                    <!-- Contact questions: free-text answers, chat style -->
                    <form v-if="isContactQuestion" class="mt-6 flex gap-2.5 generating-appear" @submit.prevent="submitContact">
                        <input-language
                            v-if="currentQuestion === 'country'"
                            class="discover-country flex-1 min-w-0"
                            label=""
                            name="country"
                            :placeholder="t('input_placeholder')"
                            @selected="pickCountry"
                        ></input-language>
                        <template v-else>
                            <input
                                ref="contactField"
                                v-model="contactInput"
                                :type="currentQuestion === 'email' ? 'email' : 'text'"
                                :name="currentQuestion"
                                maxlength="255"
                                :placeholder="t('input_placeholder')"
                                class="flowie-example-bubble flex-1 min-w-0 border border-gray-200 bg-white/70 px-4 py-2.5 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-myflowPurple/40"
                            />
                            <button
                                type="submit"
                                class="px-5 py-2.5 text-white rounded-xl bg-myflowPurple shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200 disabled:opacity-40 disabled:hover:translate-y-0"
                                :disabled="!contactInputValid"
                            >
                                {{ t('input_send') }}
                            </button>
                        </template>
                    </form>

                    <!-- Thinking skeleton (borrowed from Flowie's generating card) -->
                    <div v-if="stage === 'thinking'" class="flex justify-start mb-3 mt-6 generating-appear">
                        <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50">
                            <div class="h-1 w-full bg-gray-200 overflow-hidden">
                                <div
                                    class="h-full bg-gradient-to-r from-wlPrimary via-wlPrimary/70 to-wlPrimary transition-all duration-700 ease-out"
                                    :style="{ width: thinkingProgress + '%' }"
                                ></div>
                            </div>
                            <div class="px-5 py-4">
                                <div class="flex items-center gap-3 mb-3">
                                    <div
                                        class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-wlPrimary to-wlPrimary/70 flex items-center justify-center"
                                        :key="thinkingIndex"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                                        </svg>
                                    </div>
                                    <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + thinkingIndex">
                                        {{ t(thinkingKeys[Math.min(thinkingIndex, thinkingKeys.length - 1)], { role: roleLabel }) }}
                                    </span>
                                </div>
                                <div class="space-y-2">
                                    <div class="flex gap-2">
                                        <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 65%"></div>
                                        <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                                    </div>
                                    <div class="flex gap-2">
                                        <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                        <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div ref="chatEnd"></div>
                </div>
            </div>

            <!-- Reveal -->
            <div v-if="stage === 'reveal'" class="pt-10 generating-appear">
                <confetti-bomb />
                <div class="text-center">
                    <ai-agent-ai-orchestrator-avatar class="w-16 h-16 mx-auto"></ai-agent-ai-orchestrator-avatar>
                    <h1 class="mt-4 text-3xl md:text-4xl font-bold text-gray-900" style="line-height: 1.2em;">
                        {{ t('reveal_heading') }}
                    </h1>
                    <p
                        v-if="showInviterKicker"
                        class="mt-2 text-sm font-semibold text-myflowPurple"
                    >
                        {{ t('invited_by', { name: welcome.sender_name }) }}
                    </p>
                </div>

                <div class="mt-10 grid gap-4 sm:grid-cols-3">
                    <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm text-center">
                        <p class="text-3xl font-bold text-myflowPurple">{{ results.hoursSaved }}</p>
                        <p class="mt-1 text-sm font-semibold text-gray-800">{{ t('stat_hours') }}</p>
                        <p class="mt-1 text-xs text-gray-500">{{ t('stat_hours_sub', { daily: dailyLabel }) }}</p>
                    </div>
                    <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm text-center">
                        <p class="text-3xl font-bold text-myflowPurple">{{ results.extraRevenue }}</p>
                        <p class="mt-1 text-sm font-semibold text-gray-800">{{ t('stat_revenue') }}</p>
                        <p class="mt-1 text-xs text-gray-500">{{ t('stat_revenue_sub', { role: roleLabel }) }}</p>
                    </div>
                    <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm text-center">
                        <p class="text-3xl font-bold text-myflowPurple">{{ results.toolSavings }}</p>
                        <p class="mt-1 text-sm font-semibold text-gray-800">{{ t('stat_tools') }}</p>
                        <p class="mt-1 text-xs text-gray-500">{{ t('stat_tools_sub') }}</p>
                    </div>
                </div>

                <div class="mt-14 text-center">
                    <h2 class="text-2xl font-bold text-gray-900">{{ t('team_heading') }}</h2>
                    <p class="mt-2 text-gray-700 font-light">{{ t('team_body', { role: roleLabel.toLowerCase() }) }}</p>
                    <div class="mt-6 flex flex-wrap justify-center gap-4">
                        <div
                            v-for="member in selectedTeam"
                            :key="member.key"
                            class="flowie-example-bubble flex flex-col items-center border px-5 py-4 shadow-sm w-32"
                            :class="member.bubbleClass"
                        >
                            <img :src="member.image" :alt="member.teammate" class="w-16 h-16 object-contain" loading="lazy" />
                            <p class="mt-2 text-xs font-semibold text-gray-800 text-center">{{ member.teammate }}</p>
                        </div>
                    </div>
                </div>

                <div class="mt-14">
                    <h2 class="text-2xl font-bold text-gray-900 text-center">
                        {{ t('starts_heading') }}
                    </h2>
                    <div class="mt-6 flex flex-wrap justify-center gap-4">
                        <div
                            v-for="member in selectedTeam"
                            :key="member.startKey"
                            class="w-64 rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                        >
                            <!-- Mini mockup of the deliverable, pure CSS -->
                            <div class="h-36 border-b px-6 pt-5 flex items-end justify-center overflow-hidden" :class="member.bubbleClass">
                                <div class="w-full max-w-[210px] rounded-t-lg bg-white shadow-md border border-b-0 border-gray-100 p-3">
                                    <template v-if="member.key === 'website'">
                                        <div class="flex gap-1 mb-2">
                                            <span class="w-1.5 h-1.5 rounded-full bg-red-300"></span>
                                            <span class="w-1.5 h-1.5 rounded-full bg-amber-300"></span>
                                            <span class="w-1.5 h-1.5 rounded-full bg-green-300"></span>
                                        </div>
                                        <div class="h-8 rounded bg-gradient-to-r from-myflowPurple/60 to-pink-300/60 mb-2"></div>
                                        <div class="h-1.5 w-3/4 rounded bg-gray-200 mb-1"></div>
                                        <div class="h-1.5 w-1/2 rounded bg-gray-200 mb-2"></div>
                                        <div class="h-4 w-14 rounded-full bg-myflowPurple/70"></div>
                                    </template>
                                    <template v-if="member.key === 'leads'">
                                        <div
                                            v-for="(avatar, i) in ['bg-pink-200', 'bg-blue-200', 'bg-amber-200']"
                                            :key="avatar"
                                            class="flex items-center gap-2"
                                            :class="i > 0 ? 'mt-2' : ''"
                                        >
                                            <span class="w-5 h-5 rounded-full flex-shrink-0" :class="avatar"></span>
                                            <div class="flex-1 min-w-0">
                                                <div class="h-1.5 rounded bg-gray-200 mb-1" :style="{ width: [70, 55, 62][i] + '%' }"></div>
                                                <div class="h-1 w-1/3 rounded bg-gray-100"></div>
                                            </div>
                                            <span class="h-3 w-8 rounded-full bg-green-200 flex-shrink-0"></span>
                                        </div>
                                    </template>
                                    <template v-if="member.key === 'newsletter'">
                                        <div class="h-6 rounded bg-gradient-to-r from-myflowPurple/50 to-pink-300/50 mb-2 flex items-center justify-center">
                                            <span class="h-1.5 w-12 rounded bg-white/80"></span>
                                        </div>
                                        <div class="h-1.5 w-full rounded bg-gray-200 mb-1"></div>
                                        <div class="h-1.5 w-full rounded bg-gray-200 mb-1"></div>
                                        <div class="h-1.5 w-2/3 rounded bg-gray-200 mb-2"></div>
                                        <div class="mx-auto h-4 w-16 rounded-full bg-myflowPurple/70"></div>
                                    </template>
                                    <template v-if="member.key === 'content'">
                                        <div class="grid grid-cols-7 gap-1">
                                            <div
                                                v-for="d in 21"
                                                :key="d"
                                                class="h-4 rounded-sm bg-gray-100 flex items-center justify-center"
                                            >
                                                <span
                                                    v-if="[2, 5, 9, 12, 16, 19].includes(d)"
                                                    class="w-1.5 h-1.5 rounded-full"
                                                    :class="d % 3 === 0 ? 'bg-pink-400' : d % 3 === 1 ? 'bg-myflowPurple/70' : 'bg-teal-400'"
                                                ></span>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-if="member.key === 'automations'">
                                        <div class="flex items-center pt-2">
                                            <span class="w-8 h-8 rounded-lg bg-myflowPurple/20 flex items-center justify-center text-sm flex-shrink-0">✉️</span>
                                            <span class="flex-1 border-t-2 border-dashed border-gray-300 mx-1.5"></span>
                                            <span class="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-sm flex-shrink-0">⏱️</span>
                                            <span class="flex-1 border-t-2 border-dashed border-gray-300 mx-1.5"></span>
                                            <span class="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center text-sm flex-shrink-0">✅</span>
                                        </div>
                                        <div class="mt-3 h-1.5 w-2/3 rounded bg-gray-200"></div>
                                        <div class="mt-1 h-1.5 w-1/2 rounded bg-gray-100"></div>
                                    </template>
                                    <template v-if="member.key === 'sales'">
                                        <div class="h-1.5 w-1/2 rounded bg-gray-300 mb-2"></div>
                                        <div class="h-1 w-full rounded bg-gray-100 mb-1"></div>
                                        <div class="h-1 w-full rounded bg-gray-100 mb-1"></div>
                                        <div class="h-1 w-3/4 rounded bg-gray-100"></div>
                                        <div class="flex items-end justify-between mt-2">
                                            <svg class="h-5 w-16 text-gray-400" viewBox="0 0 40 20" fill="none">
                                                <path d="M2 15 C8 5, 12 20, 18 10 S30 12, 38 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                            </svg>
                                            <span class="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-[10px] text-green-600 font-bold">✓</span>
                                        </div>
                                    </template>
                                </div>
                            </div>
                            <div class="flex items-center gap-3 px-4 py-3">
                                <img :src="member.image" :alt="member.teammate" class="w-9 h-9 object-contain flex-shrink-0" loading="lazy" />
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-gray-800 leading-snug">{{ t(member.startKey) }}</p>
                                    <p class="text-xs text-gray-500">{{ member.teammate }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="availableTeam.length" class="mt-8 text-center">
                    <h3 class="text-sm font-semibold text-gray-800">{{ t('other_team_heading') }}</h3>
                    <p class="mt-1 text-xs text-gray-500">{{ t('other_team_body') }}</p>
                    <div class="mt-4 flex flex-wrap justify-center gap-3">
                        <div
                            v-for="member in availableTeam"
                            :key="member.key"
                            class="flowie-example-bubble flex flex-col items-center border border-gray-200 bg-white/70 px-3 py-3 w-24 opacity-70 grayscale-[40%] hover:opacity-100 hover:grayscale-0 transition-all duration-200"
                        >
                            <img :src="member.image" :alt="member.teammate" class="w-10 h-10 object-contain" loading="lazy" />
                            <p class="mt-1.5 text-[11px] font-medium text-gray-700 text-center leading-tight">{{ member.teammate }}</p>
                        </div>
                    </div>
                </div>

                <div class="mt-14 flex flex-col items-center">
                    <p class="text-xs text-gray-400 italic">{{ t('disclaimer', { role: roleLabel }) }}</p>
                    <button type="button" class="mt-2 text-xs text-gray-400 underline" @click="restart">
                        {{ t('restart') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Floating CTA, always visible on the reveal step -->
        <div
            v-if="stage === 'reveal'"
            class="fixed bottom-0 inset-x-0 z-50 border-t border-gray-100 bg-white/85 backdrop-blur py-3 px-4"
        >
            <div class="max-w-2xl mx-auto flex items-center justify-center gap-4">
                <a :href="registerLink">
                    <button class="px-8 py-3 text-white rounded-xl bg-myflowPurple text-lg shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200">
                        {{ t('cta') }}
                    </button>
                </a>
                <span class="hidden sm:inline text-sm text-gray-500">{{ t('cta_sub') }}</span>
            </div>
        </div>
    </div>
</template>
<style scoped>
.flowie-example-bubble {
    border-radius: 18px 18px 18px 8px;
    backdrop-filter: blur(10px);
}

/* Make the shared input-language component blend into the chat */
.discover-country :deep(.mb-4) {
    margin-bottom: 0;
}
.discover-country :deep(input[type='text']) {
    border-radius: 18px 18px 18px 8px;
    background-color: rgb(255 255 255 / 0.7);
    border-color: rgb(229 231 235);
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    margin-top: 0;
}
.discover-country :deep(input[type='text']:focus) {
    outline: none;
    box-shadow: 0 0 0 2px rgb(120 42 218 / 0.4);
}

.typing-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background-color: rgb(156 163 175);
    animation: typing-bounce 1s ease-in-out infinite;
    display: inline-block;
}

@keyframes typing-bounce {
    0%, 100% {
        transform: translateY(0);
        opacity: 0.4;
    }
    50% {
        transform: translateY(-4px);
        opacity: 1;
    }
}

@keyframes skeleton-shimmer {
    0%, 100% {
        opacity: 0.4;
    }
    50% {
        opacity: 1;
    }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% {
        transform: scale(0) rotate(-20deg);
    }
    60% {
        transform: scale(1.15) rotate(5deg);
    }
    100% {
        transform: scale(1) rotate(0deg);
    }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from {
        opacity: 0;
        transform: translateX(-8px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
